import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartOptions, ChartType, ChartDataSets } from "chart.js";
import { Label, Color } from "ng2-charts";

import { ZoneComponent } from "@/shared-module/components";
import { MultiChartDatasource } from "@/shared-module/models";
import { DialogService } from "@/shared-module/services";

@Component({
  selector: "app-multichart",
  templateUrl: "multichart.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class MultiChartComponent implements ZoneComponent, OnInit {
  constructor(
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogService
  ) {}
  @Input() data: MultiChartDatasource;
  @Input() width: number;
  @Input() needpaddingLeft: boolean;
  @Input() isZoom: boolean;
  @ViewChild("template", { static: true }) template;

  public barChartLabels: Label[] = [];
  public barChartType: ChartType = "bar";
  public barChartLegend = true;
  public barChartData: ChartDataSets[] = [];
  public lineChartColors: Color[] = [];

  public barChartOptions: ChartOptions = {
    aspectRatio: 1.5,
    tooltips: {
      callbacks: {
        title: (items, data) => {
          return this.data.xLabels[items[0].index];
        },
        label: (tooltipItem, data) => {
          let label = data.datasets[tooltipItem.datasetIndex].label || "";

          if (label) {
            label += ": ";
          }
          label +=
            this.data.series[tooltipItem.datasetIndex].yValues[
              tooltipItem.index
            ].yLabel;
          return label;
        },
      },
    },
    responsive: true,
    scales: {
      xAxes: [
        {
          id: "x-axis-0",
          offset: true,
          gridLines: {
            drawBorder: false,
            zeroLineColor: "transparent",
            display: false,
          },
        },
      ],
      yAxes: [
        {
          id: "y-axis-0",
          ticks: {
            beginAtZero: true,
            callback: (value: number) => {
              if (this.getRoundedValue(value) === value || value > 0) {
                if (this.data.axisY.unit)
                  return value + " " + this.data.axisY.unit;
                return value;
              }
              return "";
            },
          },
          gridLines: {
            drawBorder: true,
            display: true,
            borderDash: [2, 5],
          },
        },
        {
          id: "y-axis-1",
          position: "right",
          ticks: {
            beginAtZero: true,
            fontColor: "#848484",
            callback: (value: number) => {
              if (this.data.secondAxisY == true)
              {
                if (value % 10 === 0 || value > 0) {
                  if (this.data.secondAxisYIsPercentage) {
                    if (this.data.secondAxisYMultiplyBy100) {
                      value = Math.round(value * 1000) / 10;
                    }
                    return value + "%";
                  }
                  else if (this.data.secondAxisYUnit != null )
                    return value + " " + this.data.secondAxisYUnit;
                  else return value;
                }
              }
              return "";
            },
          },
          gridLines: {
            drawBorder: false,
            display: false,
          },
        },
      ],
    },
  };

  zoom() {
    this.dialogService.setZoomInfo(this.data.zoomInfo);
  }

  getRoundedValue(val: number) {
    let roundedValue = 0;
    let sign = 1;
    let multipleOf10 = 1;
    if (val < 0) {
      sign = -1;
      val = Math.abs(val);
    }
    if (val > 0) {
      if (val < 0.1) {
        multipleOf10 = -2;
      } else if (val < 1) {
        multipleOf10 = -1;
      } else if (val < 10) {
        multipleOf10 = 0;
      } else if (val < 100) {
        multipleOf10 = 1;
      } else if (val < 1000) {
        multipleOf10 = 2;
      } else if (val < 10000) {
        multipleOf10 = 3;
      } else if (val < 100000) {
        multipleOf10 = 4;
      } else if (val < 1000000) {
        multipleOf10 = 5;
      }
    }

    roundedValue =
      Math.ceil(val / Math.pow(10, multipleOf10)) * Math.pow(10, multipleOf10);
    return roundedValue * sign;
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);


    if (this.data) {
      // Determine the scale factor for constraints
      // If data is in percentage mode, it's stored in decimal form (0-1 range)
      // regardless of secondAxisYMultiplyBy100 (that only affects display)
      // So max constraint is 1 for percentages, 100 otherwise
      const isDecimalScale = this.data.secondAxisYIsPercentage;
      const maxConstraint = isDecimalScale ? 1 : 100;
      const minConstraint = 0; // Min is always 0 regardless of scale

      // Set default min/max values for second Y-axis if not set to free
      // These will be overridden later based on actual data if the "free" properties are set
      if (!this.data.secondAxisYMaxFree && this.barChartOptions.scales?.yAxes?.[1]?.ticks) {
        this.barChartOptions.scales.yAxes[1].ticks.max = maxConstraint;
      }
      if (!this.data.secondAxisYMinFree && this.barChartOptions.scales?.yAxes?.[1]?.ticks) {
        this.barChartOptions.scales.yAxes[1].ticks.min = minConstraint;
      }

      this.barChartData = [];
      this.lineChartColors = [];
      this.barChartLabels = [];
      let data = [];
      let ds = {};
      let color = {};

      this.data.xValues.forEach((v) => {
        this.barChartLabels.push(`P${v}`);
      });

      let minBar = 0;
      let maxBar = 0;
      let minLine = 0;
      let maxLine = 0;
      let firstLine = true;
      let barThickness = 15;
      if (this.data.zoomInfo == null) barThickness = 20;

      // Store constraints for use in line processing
      const yAxisMaxConstraint = maxConstraint;
      const yAxisMinConstraint = minConstraint;

      this.data.series.forEach((s) => {
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        data = [];
        s.yValues.forEach((v) => {
          if (v.y < min) {
            min = v.y;
          }
          if (v.y > max) {
            max = v.y;
          }
          data.push(v.y);
        });

        // If no values were found, reset to 0
        if (min === Number.POSITIVE_INFINITY) min = 0;
        if (max === Number.NEGATIVE_INFINITY) max = 0;

        if (s.type === "bar") {
          // tslint:disable-next-line:object-literal-shorthand
          ds = {
            data: data,
            label: s.caption,
            fill: false,
            stack: s.group,
            type: "bar",
            barThickness: barThickness,
            order: 1,
          };
          if (min < minBar) {
            minBar = min;
          }
          if (max > maxBar) {
            maxBar = max;
          }
        } else {
          if (this.data.secondAxisY) {
            // tslint:disable-next-line:object-literal-shorthand
            ds = {
              data: data,
              label: s.caption,
              fill: false,
              type: "line",
              yAxisID: "y-axis-1",
              order: 0,
            };
            if (min < minLine) {
              minLine = min;
            }
            if (max > maxLine) {
              maxLine = max;
            }
          } else {
            // tslint:disable-next-line:object-literal-shorthand
            ds = {
              data: data,
              label: s.caption,
              fill: false,
              type: "line",
              order: 0,
            };
          }
        }

        color = {
          // grey
          backgroundColor: s.color,
          borderColor: s.color,
          pointBackgroundColor: s.color,
          pointBorderColor: s.color,
          pointHoverBackgroundColor: s.color,
          pointHoverBorderColor: s.color,
          borderWidth: 1,
        };

        this.barChartData.push(ds);
        this.lineChartColors.push(color);

        if (s.type != "bar") {
          if (!this.data.secondAxisY) {
            this.barChartOptions.scales.yAxes[1].display = false;
          } else {
            const yAxis1Ticks = this.barChartOptions.scales?.yAxes?.[1]?.ticks;
            if (yAxis1Ticks) {
              // Handle min value with constraint: if secondAxisYMinFree is true, allow computed min but ensure it's >= yAxisMinConstraint
              if (this.data.secondAxisYMinFree) {
                // For decimal scale (percentages), round to 2 decimal places (e.g., 0.37 -> 0.30)
                // For integer scale, use Math.floor
                let computedMin: number;
                if (isDecimalScale) {
                  // Round down to nearest 0.1 (10%) for better visualization
                  computedMin = Math.floor(min * 10) / 10;
                } else {
                  computedMin = Math.floor(min);
                }

                if (firstLine || (yAxis1Ticks.min !== undefined && computedMin > yAxis1Ticks.min)) {
                  yAxis1Ticks.min = Math.max(yAxisMinConstraint, computedMin);
                }
              } else {
                if (firstLine || (yAxis1Ticks.min !== undefined && Math.floor(min) < yAxis1Ticks.min)) {
                  yAxis1Ticks.min = Math.floor(min);
                }
              }

              // Handle max value with constraint: if secondAxisYMaxFree is true, allow computed max but ensure it's <= yAxisMaxConstraint
              if (this.data.secondAxisYMaxFree) {
                // For decimal scale (percentages), round to 2 decimal places (e.g., 0.37 -> 0.40)
                // For integer scale, use Math.ceil
                let computedMax: number;
                if (isDecimalScale) {
                  // Round up to nearest 0.1 (10%) for better visualization
                  computedMax = Math.ceil(max * 10) / 10;
                } else {
                  computedMax = Math.ceil(max);
                }

                if (firstLine || (yAxis1Ticks.max !== undefined && computedMax < yAxis1Ticks.max)) {
                  yAxis1Ticks.max = Math.min(yAxisMaxConstraint, computedMax);
                }
              } else {
                if (firstLine || (yAxis1Ticks.max !== undefined && Math.ceil(max) > yAxis1Ticks.max)) {
                  yAxis1Ticks.max = Math.ceil(max);
                }
              }

              firstLine = false;
            }
          }
        }
      });

      if (minBar < 0 || minLine < 0) {
        maxBar = this.getRoundedValue(maxBar);
        minBar = this.getRoundedValue(minBar);
        maxLine = this.getRoundedValue(maxLine);
        minLine = this.getRoundedValue(minLine);

        const barRatio = maxBar / (maxBar - minBar);
        const lineRatio = maxLine / (maxLine - minLine);

        if (this.data.secondAxisY && !this.data.secondAxisYFree && !isNaN(lineRatio)) {
          if (lineRatio > barRatio) {
            // Apply constraints when setting min/max for second axis
            let computedMin = (maxLine / maxBar) * minBar;
            let computedMax = maxLine;

            if (this.data.secondAxisYMinFree) {
              computedMin = Math.max(yAxisMinConstraint, computedMin);
            }
            if (this.data.secondAxisYMaxFree) {
              computedMax = Math.min(yAxisMaxConstraint, computedMax);
            }

            this.barChartOptions.scales.yAxes[1].ticks.min = computedMin;
            this.barChartOptions.scales.yAxes[1].ticks.max = computedMax;

            this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
            this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
          } else {
            // Apply constraints when setting min/max for second axis
            let computedMin = minLine;
            let computedMax = maxLine;

            if (this.data.secondAxisYMinFree) {
              computedMin = Math.max(yAxisMinConstraint, computedMin);
            }
            if (this.data.secondAxisYMaxFree) {
              computedMax = Math.min(yAxisMaxConstraint, computedMax);
            }

            this.barChartOptions.scales.yAxes[1].ticks.min = computedMin;
            this.barChartOptions.scales.yAxes[1].ticks.max = computedMax;

            this.barChartOptions.scales.yAxes[0].ticks.min = (minBar > 0) ? 0 : minBar;
            this.barChartOptions.scales.yAxes[0].ticks.max = (maxBar < 0) ? 0 : maxBar;
          }
        }
        if (!this.data.secondAxisY) {
          this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
          this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
        }
        if (this.data.secondAxisY && this.data.secondAxisYFree) {
          this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
          this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;

          // Apply constraints when setting min/max for second axis
          let computedMin = minLine;
          let computedMax = maxLine;

          if (this.data.secondAxisYMinFree) {
            computedMin = Math.max(yAxisMinConstraint, computedMin);
          }
          if (this.data.secondAxisYMaxFree) {
            computedMax = Math.min(yAxisMaxConstraint, computedMax);
          }

          this.barChartOptions.scales.yAxes[1].ticks.min = computedMin;
          this.barChartOptions.scales.yAxes[1].ticks.max = computedMax;
        }
      }
      // Apply explicit min/max from data.axisY if specified
      if (this.data.axisY) {
        const yAxis0Ticks = this.barChartOptions.scales?.yAxes?.[0]?.ticks;
        if (yAxis0Ticks) {
          if (this.data.axisY.min !== undefined && this.data.axisY.min !== null) {
            yAxis0Ticks.min = this.data.axisY.min;
          }
          if (this.data.axisY.max !== undefined && this.data.axisY.max !== null) {
            yAxis0Ticks.max = this.data.axisY.max;
          }
        }
      }
    }
  }
}
